(() => {
  const BRAND_OPTION = 'Brand';
  const DEVICE_STORAGE_KEY = 'dyefor:selected-device';

  const clean = (value) => String(value || '').trim();

  const getBrand = (value, context) => {
    const label = `${clean(value)} ${clean(context)}`.toLowerCase();

    if (/iphone|airpod|ipad|macbook|\bapplev2\b|\bapple\b/.test(label)) return 'Apple';
    if (/samsung|galaxy|\bs\d{2}\b|\ba\d{2}\b|xcover/.test(label)) return 'Samsung';
    if (/pixel|google/.test(label)) return 'Google';
    if (/huawei|\bp\d{2}\b|\bmate\b/.test(label)) return 'Huawei';
    if (/oneplus/.test(label)) return 'OnePlus';
    if (/xperia|sony/.test(label)) return 'Sony';
    if (/nokia/.test(label)) return 'Nokia';
    return 'Other';
  };

  const unique = (values) => [...new Set(values)];

  class DyeforDevicePicker {
    constructor(variantSelects) {
      this.variantSelects = variantSelects;
      this.context = variantSelects.dataset.deviceContext || '';
      this.deviceWrapper = this.findDeviceOptionWrapper();
      this.brandWrapper = this.findOptionWrapper(BRAND_OPTION);
      this.deviceSelect = this.deviceWrapper?.querySelector('select');
      this.brandSelect = this.brandWrapper?.querySelector('select');
      this.makeSelect = this.findMakeSelect();
      this.makeWrapper = this.makeSelect?.closest('.product-form__input');
      this.isOpen = false;

      if (!this.deviceSelect || variantSelects.dataset.devicePickerReady === 'true') return;

      this.allDevices = Array.from(this.deviceSelect.options).map((option) => ({
        value: option.value,
        label: clean(option.textContent),
        disabled: option.disabled,
      }));
      this.build();
      this.bind();
      this.applyRequestedDevice();
      this.refresh();
      variantSelects.dataset.devicePickerReady = 'true';
    }

    findOptionWrapper(name) {
      return Array.from(this.variantSelects.querySelectorAll('.product-form__input[data-option_name]'))
        .find((wrapper) => clean(wrapper.dataset.option_name).toLowerCase() === name.toLowerCase());
    }

    findDeviceOptionWrapper() {
      return Array.from(this.variantSelects.querySelectorAll('.product-form__input[data-option_name]'))
        .find((wrapper) => {
          const optionName = clean(wrapper.dataset.option_name).toLowerCase();
          return optionName === 'phone case'
            || optionName.includes('device')
            || optionName.includes('model')
            || optionName.includes('airpod')
            || optionName.includes('ipad')
            || optionName.includes('macbook');
        });
    }

    findMakeSelect() {
      const previous = this.variantSelects.previousElementSibling?.querySelector('.make-select');
      return previous || this.variantSelects.parentElement?.querySelector('.make-select') || null;
    }

    build() {
      const picker = this.variantSelects.querySelector('.dyefor-device-picker[data-prerendered]') || document.createElement('div');
      picker.className = 'dyefor-device-picker';
      if (!picker.hasAttribute('data-prerendered')) picker.innerHTML = `
        <button class="dyefor-device-picker__summary" type="button" aria-expanded="false">
          <span class="dyefor-device-picker__brand"></span>
          <span class="dyefor-device-picker__model"></span>
          <span class="dyefor-device-picker__toggle" aria-hidden="true">
            <span class="dyefor-device-picker__chevron"></span>
          </span>
        </button>
        <div class="dyefor-device-picker__panel" hidden>
          <div class="dyefor-device-picker__brands" aria-label="Choose device brand"></div>
          <div class="dyefor-device-picker__models" role="listbox" aria-label="Choose device model"></div>
        </div>
      `;

      this.picker = picker;
      this.summary = picker.querySelector('.dyefor-device-picker__summary');
      this.brandLabel = picker.querySelector('.dyefor-device-picker__brand');
      this.modelLabel = picker.querySelector('.dyefor-device-picker__model');
      this.panel = picker.querySelector('.dyefor-device-picker__panel');
      this.brands = picker.querySelector('.dyefor-device-picker__brands');
      this.models = picker.querySelector('.dyefor-device-picker__models');

      const panelId = `DyeforDevicePicker-${this.variantSelects.dataset.section || Math.random().toString(36).slice(2)}`;
      this.panel.id = panelId;
      this.summary.setAttribute('aria-controls', panelId);

      this.deviceWrapper.before(picker);
      this.deviceWrapper.classList.add('dyefor-device-picker-source-hidden');
      this.brandWrapper?.classList.add('dyefor-device-picker-source-hidden');
      this.makeWrapper?.classList.add('dyefor-device-picker-source-hidden');
    }

    bind() {
      this.summary.addEventListener('click', () => this.setOpen(!this.isOpen));

      this.variantSelects.addEventListener('change', () => {
        window.requestAnimationFrame(() => this.refresh());
      });

      this.onVariantChanged = () => window.requestAnimationFrame(() => this.refresh());
      document.addEventListener('variantchanged', this.onVariantChanged);

      this.onDocumentClick = (event) => {
        if (this.isOpen && !this.picker.contains(event.target)) this.setOpen(false);
      };
      document.addEventListener('click', this.onDocumentClick);

      this.onKeyDown = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.summary.focus();
        }
      };
      this.picker.addEventListener('keydown', this.onKeyDown);

      if (this.makeSelect) {
        this.makeObserver = new MutationObserver(() => this.refresh());
        this.makeObserver.observe(this.makeSelect, { childList: true });
      }
    }

    setOpen(open) {
      this.isOpen = Boolean(open);
      this.picker.classList.toggle('is-open', this.isOpen);
      this.summary.setAttribute('aria-expanded', String(this.isOpen));
      this.panel.hidden = !this.isOpen;
    }

    availableDevices() {
      if (this.brandSelect) {
        return Array.from(this.deviceSelect.options).map((option) => ({
          value: option.value,
          label: clean(option.textContent),
          disabled: option.disabled,
          available: true,
        }));
      }

      const currentOptions = new Map(
        Array.from(this.deviceSelect.options).map((option) => [option.value, option.disabled])
      );

      return this.allDevices.map((device) => ({
        ...device,
        disabled: device.disabled || currentOptions.get(device.value) === true,
        available: currentOptions.has(device.value),
      }));
    }

    selectedBrand() {
      if (this.makeSelect?.selectedOptions.length) {
        return clean(this.makeSelect.selectedOptions[0].textContent);
      }
      if (this.brandSelect) return clean(this.brandSelect.value);
      return getBrand(this.deviceSelect.value, this.context);
    }

    brandNames(devices) {
      if (this.makeSelect?.options.length) {
        return Array.from(this.makeSelect.options).map((option) => clean(option.textContent));
      }
      if (this.brandSelect) {
        return Array.from(this.brandSelect.options).map((option) => clean(option.value));
      }
      return unique(devices.map((device) => getBrand(device.value, this.context)));
    }

    renderBrands(devices, selectedBrand) {
      const brandNames = this.brandNames(devices).filter(Boolean);
      this.brands.replaceChildren();

      if (brandNames.length < 2) return;

      brandNames.forEach((brand) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dyefor-device-picker__brand-button';
        button.textContent = brand;
        button.setAttribute('aria-pressed', String(brand === selectedBrand));
        button.addEventListener('click', () => this.selectBrand(brand, devices));
        this.brands.append(button);
      });
    }

    prioritisePreorderModels(devices) {
      const form = document.getElementById(`product-form-${this.variantSelects.dataset.section}`);
      if (!form?.dataset.preorderVariantIds) return devices;

      let ids;
      try {
        ids = JSON.parse(form.dataset.preorderVariantIds);
      } catch (error) {
        return devices;
      }
      if (!Array.isArray(ids)) return devices;

      const deviceIndex = Array.from(this.variantSelects.querySelectorAll('select')).indexOf(this.deviceSelect);
      const variants = this.variantSelects.getVariantData?.();
      if (deviceIndex < 0 || !Array.isArray(variants)) return devices;
      const preorderIds = new Set(ids.filter((id) => /^\d+$/.test(String(id))).map(String));
      const models = new Set(variants
        .filter((variant) => preorderIds.has(String(variant.id)))
        .map((variant) => variant.options?.[deviceIndex]));

      // Only configured preorder models move; preserve the other models' order.
      return devices.filter((device) => models.has(device.value))
        .concat(devices.filter((device) => !models.has(device.value)));
    }

    renderModels(devices, selectedBrand) {
      const visibleDevices = this.brandSelect
        ? devices
        : devices.filter((device) => getBrand(device.value, this.context) === selectedBrand);

      this.models.replaceChildren();
      this.prioritisePreorderModels(visibleDevices).forEach((device) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dyefor-device-picker__model-button';
        button.textContent = device.label;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(device.value === this.deviceSelect.value));
        button.disabled = !device.available || device.disabled;
        button.addEventListener('click', () => this.selectDevice(device.value));
        this.models.append(button);
      });
    }

    selectBrand(brand, devices) {
      if (this.makeSelect?.options.length) {
        const matchingOption = Array.from(this.makeSelect.options)
          .find((option) => clean(option.textContent).toLowerCase() === brand.toLowerCase());
        if (!matchingOption) return;
        if (matchingOption.value === this.makeSelect.value) return;
        this.makeSelect.value = matchingOption.value;
        this.makeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      if (this.brandSelect) {
        const matchingOption = Array.from(this.brandSelect.options)
          .find((option) => clean(option.value).toLowerCase() === brand.toLowerCase());
        if (!matchingOption) return;
        this.brandSelect.value = matchingOption.value;
        this.brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const firstDevice = devices.find((device) => (
        getBrand(device.value, this.context) === brand && device.available && !device.disabled
      ));
      if (firstDevice) this.selectDevice(firstDevice.value, false);
    }

    selectDevice(value, close = true) {
      const option = Array.from(this.deviceSelect.options).find((item) => item.value === value);
      if (!option || option.disabled) return;

      this.deviceSelect.value = value;
      this.storeDevice(value);
      this.deviceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      if (close) this.setOpen(false);
    }

    requestedDevice() {
      const params = new URLSearchParams(window.location.search);
      const queryDevice = params.get('device');
      if (queryDevice) return clean(queryDevice);
      if (params.has('variant')) return '';
      try {
        const remembered = clean(window.localStorage.getItem(DEVICE_STORAGE_KEY));
        if (remembered) return remembered;
      } catch (error) {
        // The default still works when browser storage is unavailable.
      }
      return this.allDevices.some((device) => device.value === 'iPhone 18 Pro Max' && !device.disabled)
        ? 'iPhone 18 Pro Max'
        : '';
    }

    storeDevice(value) {
      try {
        window.localStorage.setItem(DEVICE_STORAGE_KEY, clean(value));
      } catch (error) {
        // Selection still works when browser storage is unavailable.
      }
    }

    applyRequestedDevice() {
      const requested = this.requestedDevice().toLowerCase();
      if (!requested) return;
      const option = Array.from(this.deviceSelect.options)
        .find((item) => clean(item.value).toLowerCase() === requested);
      if (!option || option.disabled) return;

      if (clean(this.deviceSelect.value).toLowerCase() === requested) {
        this.storeDevice(option.value);
        return;
      }

      this.deviceSelect.value = option.value;
      this.storeDevice(option.value);
      this.deviceSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    refresh() {
      if (!this.deviceSelect.isConnected) return;

      const devices = this.availableDevices();
      const selectedBrand = this.selectedBrand();
      const selectedDevice = devices.find((device) => device.value === this.deviceSelect.value);

      this.brandLabel.textContent = selectedBrand || 'Choose';
      this.modelLabel.textContent = selectedDevice?.label || clean(this.deviceSelect.value) || 'Choose your device';
      this.summary.setAttribute(
        'aria-label',
        `Choose device model. Current selection: ${this.brandLabel.textContent} ${this.modelLabel.textContent}`
      );
      this.renderBrands(devices, selectedBrand);
      this.renderModels(devices, selectedBrand);
    }
  }

  const initialise = (root = document) => {
    root.querySelectorAll('variant-selects[data-dyefor-device-picker]').forEach((variantSelects) => {
      new DyeforDevicePicker(variantSelects);
    });
  };

  if (document.readyState === 'complete') {
    initialise();
  } else {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
